/**
 * @file moderationNotice.js
 * @description The one place that asks an edge function to send a moderation or compliance notice (suspension,
 *   flag, verification result, privacy-request answer, data export) and turns its answer into something an admin can
 *   read. The functions answer with the real outcome of the email (see supabase/functions/_shared/compliance/
 *   sendOutcome.ts, the server engine this mirrors for display only):
 *
 *     ok:false + error                           the notice did not go (flag, verification result, privacy answer)
 *     email_sent: false (not skipped)            the email did not go (email_error says why)
 *     notification_ok: false                     the in-app notice was not saved
 *     applicant_notices_ok: false                the providers with live applications were not told
 *     support_notice_sent: false                 the data export's support copy did not go
 *     skipped: "no_email_on_file"                a privacy answer with nobody to email
 *     skipped: "account_gone"                    a privacy answer whose account no longer exists (never emailed)
 *
 *   noticeProblem(data, error) returns "" when everything went, otherwise a short readable reason. invokeNotice()
 *   calls the function and returns that text, so a caller only decides where to show it.
 * @module lib/moderationNotice
 */
import { supabase } from "./supabase";
import { functionErrorMessage } from "./functionErrorMessage";

/** Readable text for the codes the compliance functions answer with. Unknown codes are shown as they are. */
export const NOTICE_REASONS = {
  not_configured: "the email service is not set up",
  no_email_on_file: "there is no email address on file",
  no_recipient: "there is no email address on file",
  agency_email_not_found: "the agency has no email address",
  recipient_lookup_failed: "the recipient could not be looked up",
  lookup_failed: "the account could not be looked up",
  transport: "the email provider refused it",
  send_failed: "the email could not be sent",
  render: "the email could not be built",
  forbidden: "only an admin can do this",
};

const reasonText = (code) => NOTICE_REASONS[code] || String(code || "unknown reason");

/** "" when the notice went, otherwise what did not. `data` is the function's answer, `error` the invoke error text. */
export function noticeProblem(data, error = "") {
  if (error) return String(error);
  if (!data || typeof data !== "object") return "";
  const parts = [];
  if (data.ok === false) {
    parts.push(`the notice did not go (${reasonText(data.error || data.reason)})`);
  } else {
    if (data.email_sent === false && !data.email_skipped) parts.push(`the email did not go (${reasonText(data.email_error)})`);
    if (data.skipped === "no_email_on_file") parts.push("the email did not go (there is no email address on file)");
    if (data.skipped === "account_gone") parts.push("the email did not go (the account no longer exists)");
    if (data.support_notice_sent === false && !data.support_notice_skipped) parts.push("the support copy did not go");
    if (data.notification_ok === false) parts.push("the in-app notice was not saved");
    if (data.applicant_notices_ok === false) parts.push("providers with applications were not told");
  }
  return parts.join("; ");
}

/** Calls a notice function and returns the problem text ("" when all went). Never throws. */
export async function invokeNotice(name, body) {
  try {
    const { data, error } = (await supabase.functions.invoke(name, { body })) || {};
    if (error) return await functionErrorMessage(error, "the request failed");
    return noticeProblem(data);
  } catch (e) {
    return String(e?.message || "the request failed");
  }
}
